import type { ComponentType, PropsWithChildren } from "react";
import { Children, cloneElement } from "react";

import type { IComponent } from "../Component";
import { defaultAttributes } from "../Component";
import type { IComponentAjax, IComponentAjaxState } from "../ComponentAjax";
import { ComponentAjax } from "../ComponentAjax";
import { ItemTemplate, NoResultsTemplate } from "./Templates";

import * as css from "./Repeater.module.css";

export * from "./Templates";

type ComponentPropsType = any;

export type IRepeater = PropsWithChildren<IComponentAjax<ComponentPropsType[]>>;

interface IRepeaterState extends IComponentAjaxState {
    components: ComponentPropsType[];
}

export class Repeater extends ComponentAjax<
    ComponentPropsType[],
    PropsWithChildren<IRepeater>,
    IRepeaterState
> {
    constructor(props: IRepeater) {
        super(props);
        this.state = {
            isLoading: !this.props.initialData && this.props.ajaxAction !== null,
            components: this.props.initialData ? this.props.initialData.slice() : [],
        };
    }

    public componentDidMount() {
        if (!this.props.initialData) {
            this.ajaxRequest();
        }
    }

    public render() {
        if (!this.props.children) {
            return null;
        }

        let noResultsTemplate = null;

        if (
            this.state.isLoading === false &&
            (!this.state.components || this.state.components.length === 0)
        ) {
            noResultsTemplate = this.getTemplate(NoResultsTemplate);
        }

        return (
            <div {...defaultAttributes(this.props, css.repeaterComponent)}>
                {this.transformTemplate()}
                {noResultsTemplate}
                {this.renderLoading()}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: IComponent[]) {
        this.setState({
            isLoading: false,
            components: response,
        });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private getTemplate = (templateType: ComponentType) => {
        if (!this.props.children) {
            return null;
        }

        const templates: JSX.Element[] = [];
        Children.forEach(this.props.children, (child: JSX.Element) => {
            if (child.type === templateType) {
                templates.push(child);
            }
        });

        let template = null;

        if (templates.length === 1) {
            [template] = templates;
        } else if (templates.length > 1) {
            console.error(
                `Repeater can only have one ${templateType.prototype.constructor.name} element defined, but found ${templates.length}`,
            );
        }

        return template;
    };

    private transformTemplate = () => {
        if (!this.state.components) {
            return null;
        }

        if (this.state.isLoading === true) {
            return null;
        }

        const template = this.getTemplate(ItemTemplate);

        return this.state.components.map(
            (prop, i) =>
                template && cloneElement(template, { key: prop.id || prop.key || i, childProps: prop }),
        );
    };
}
